import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/16px/icon_16px_download.svg';
import { ReactComponent as PinIcon } from '@repo/common/assets/icons/16px/icon_16px_pin-right_outline.svg';
import { ReactComponent as FolderIcon } from '@repo/common/assets/icons/16px/icon_16px_folder_outline.svg';
import { getBoardResources } from '../../api/twoDays/board.api';
import TdButton from './Button';
import { cn, stateLabelOf } from './model/derive';
import { MOCK_RESOURCES } from './model/mockResources';
import { approvedVersionOf } from './model/relay';
import { extOf, formatSize } from './fileUtils';
import { VersionBody } from './VersionLog';

/** 태스크 전체 페이지 — 릴레이 · 다음 행동 · Inputs · 모바일 요약 · 모바일 하단 바 */

const approvedAt = (t) => t.review?.at ?? t.approvedSubmittedAt;

/* ── 릴레이 스테퍼 (W2 태스크 페이지 · Ops O3 공통) ──────────────────────────
 * 화살표 칸: 높이 최소 64 · 화살 12px · 칸 사이 -8px 겹침 · 첫 칸 왼쪽 · 마지막 칸 오른쪽은 평평 · 칸 최소 폭 176 · 가로 스크롤(스크롤바 숨김)
 *   Previous / Next  흰 바탕 + 1px green-deep03 16% · 라벨 12 secondary · 제목 13/500 · 메타 12 (승인 = success04) · 누르면 그 태스크
 *   This task        green-deep03 8% 바탕 + 1.6px 40% · "This task" 12/500 green-deep04 · 담당 → 받는 사람 13/500 · 상태 12
 *   앞뒤 없음         gray01 + 24% 테두리 · "Start of relay" / "End of relay" · "Team lead Kim" 12/500 tertiary
 * 테두리는 바깥 칸(테두리 색)과 같은 모양의 안쪽 칸(바탕 색)을 겹쳐 그린다 — clip-path 칸에는 border 가 안 그려져서.
 */
const A = 12;
const CLIP = {
  only: 'none',
  first: `polygon(0 0, calc(100% - ${A}px) 0, 100% 50%, calc(100% - ${A}px) 100%, 0 100%)`,
  mid: `polygon(0 0, calc(100% - ${A}px) 0, 100% 50%, calc(100% - ${A}px) 100%, 0 100%, ${A}px 50%)`,
  last: `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${A}px 50%)`,
};
const LOOK = {
  node: { edge: 'rgba(0,158,79,0.16)', fill: '#ffffff', inset: 1 },
  cur: { edge: 'rgba(0,158,79,0.40)', fill: 'color-mix(in srgb, #009e4f 8%, #ffffff)', inset: 1.6 },
  end: { edge: 'rgba(0,158,79,0.24)', fill: '#fcfaf7', inset: 1 },
};

/**
 * @param items [{ key, kind: 'node' | 'cur' | 'end', label, title, meta, metaOk, to, onClick }]
 */
export const ChevronRelay = ({ items, className }) => {
  const n = items.length;
  return (
    <nav aria-label='Relay' className={cn('flex items-stretch overflow-x-auto pb-1 pt-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', className)}>
      {items.map((it, i) => {
        const first = i === 0;
        const last = i === n - 1;
        const clip = n === 1 ? CLIP.only : first ? CLIP.first : last ? CLIP.last : CLIP.mid;
        const look = LOOK[it.kind] ?? LOOK.node;
        const style = {
          clipPath: clip,
          background: look.edge,
          marginLeft: first ? 0 : -8,
          paddingLeft: first ? 14 : A + 10,
          paddingRight: last ? 14 : A + 8,
        };
        const inner = (
          <>
            <span aria-hidden style={{ clipPath: clip, inset: look.inset, background: look.fill }} className='absolute' />
            <span className='relative flex h-full min-w-0 flex-col justify-center gap-0.5'>
              <span
                className={cn(
                  'text-[12px] leading-[140%]',
                  it.kind === 'cur' ? 'font-medium text-green-deep04' : it.kind === 'end' ? 'font-medium text-text-tertiary' : 'text-text-secondary',
                )}
              >
                {it.label}
              </span>
              {it.title && <span className='truncate text-[13px] font-medium leading-[140%] text-text-primary'>{it.title}</span>}
              {it.meta && (
                <span className={cn('truncate text-[12px] leading-[140%]', it.metaOk ? 'text-functional-success04' : 'text-text-secondary')}>{it.meta}</span>
              )}
            </span>
          </>
        );
        const base = 'relative min-h-[64px] min-w-[176px] flex-1 shrink-0 py-2 text-left';
        if (it.kind === 'node' && (it.to || it.onClick)) {
          return it.to ? (
            <Link key={it.key} to={it.to} title={`Open ${it.title}`} style={style} className={cn(base, 'hover:brightness-[0.98]')}>
              {inner}
            </Link>
          ) : (
            <button key={it.key} type='button' onClick={it.onClick} title={`Open ${it.title}`} style={style} className={cn(base, 'hover:brightness-[0.98]')}>
              {inner}
            </button>
          );
        }
        return (
          <div key={it.key} aria-current={it.kind === 'cur' ? 'step' : undefined} style={style} className={base}>
            {inner}
          </div>
        );
      })}
    </nav>
  );
};

/** 태스크 하나를 칸으로 — "담당 · 상태" (승인이면 "Approved 시각") */
export const relayNode = (task, label, { name, fmt, to }) => {
  const ok = task.state === 'approved';
  return {
    key: task.id,
    kind: 'node',
    label,
    title: task.title,
    meta: `${name(task.assigneeId)} · ${ok ? `Approved ${fmt(approvedAt(task))}` : stateLabelOf(task)}`,
    metaOk: ok,
    to: to(task.id),
  };
};

export const RelayStrip = ({ task, prevs, nexts, name, fmt, to }) => {
  const items = [
    ...(prevs.length
      ? prevs.map((p) => relayNode(p, 'Previous', { name, fmt, to }))
      : [{ key: 'start', kind: 'end', label: 'Start of relay', meta: 'No earlier task' }]),
    { key: 'cur', kind: 'cur', label: 'This task', title: `${name(task.assigneeId)} → ${name(task.receiverId)}`, meta: stateLabelOf(task) },
    ...(nexts.length
      ? nexts.map((x) => relayNode(x, 'Next', { name, fmt, to }))
      : [{ key: 'end', kind: 'end', label: task.isFinal ? 'Team lead Kim' : 'End of relay', meta: task.isFinal ? 'Final review' : 'No later task' }]),
  ];
  return <ChevronRelay items={items} />;
};

const TONE = {
  act: ['bg-surface-green10 border-green-deep03/[0.24]', 'bg-green-deep02', 'text-text-primary'],
  warn: [
    'bg-functional-warning01 border-functional-warning02',
    'bg-functional-warning03',
    'text-functional-warning04',
  ],
  wait: ['bg-grayscale-gray00 border-green-deep03/[0.12]', 'bg-functional-accent03', 'text-text-primary'],
  done: ['bg-grayscale-gray00 border-green-deep03/[0.12]', 'bg-functional-success03', 'text-text-primary'],
  ro: ['bg-grayscale-gray00 border-green-deep03/[0.12]', 'bg-grayscale-gray05', 'text-text-primary'],
};

export const NextAction = ({
  banner,
  buttons,
  locked,
  lockTip,
  className,
  innerRef,
  children,
  ...rest
}) => {
  const tone = TONE[banner.tone] ?? TONE.ro;
  return (
    <section
      ref={innerRef}
      aria-label='Next action'
      className={cn('flex flex-col gap-3.5 rounded-2xl border px-5 py-4', tone[0], className)}
      {...rest}
    >
      <div className='flex items-start gap-3'>
        <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', tone[1])} />
        <div className='flex min-w-0 flex-col gap-0.5'>
          <span className='text-[12px] font-medium leading-[140%] text-text-secondary'>
            {banner.eyebrow}
          </span>
          <span className={cn('text-[17px] font-medium leading-[140%] [text-wrap:pretty]', tone[2])}>
            {banner.title}
          </span>
          {banner.text && (
            <span className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
              {banner.text}
            </span>
          )}
        </div>
      </div>
      {buttons.length > 0 && (
        <div className='flex flex-wrap items-center gap-2 pl-5'>
          {buttons.map((b) => (
            <TdButton
              key={b.label}
              variant={b.kind === 'primary' ? 'primary' : b.kind === 'secondary' ? 'soft' : 'ghost'}
              onClick={b.onClick}
              disabled={b.disabled || locked}
              title={(locked ? lockTip : b.tip) || undefined}
              aria-describedby={b.highlight && !locked ? 'contributor-tip' : undefined}
              className={cn(
                b.kind === 'text' && 'px-3 font-normal',
                b.highlight && !locked && 'ring-2 ring-green-deep02 ring-offset-1',
              )}
            >
              {b.label}
            </TdButton>
          ))}
        </div>
      )}
      {children}
    </section>
  );
};

const InputCard = ({ head, meta, children }) => (
  <div className='flex flex-col overflow-hidden rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00'>
    <div className='flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2.5 pt-3'>
      {head}
      {meta && <span className='text-[12px] leading-[140%] text-text-secondary'>{meta}</span>}
    </div>
    {children}
  </div>
);

const pill =
  'flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-green-deep03/[0.08] px-3.5 text-[13px] font-medium text-text-primary hover:bg-green-deep03/[0.14]';

const InputRow = ({ icon, title, sub, action }) => (
  <div className='flex min-h-[56px] items-center gap-3 border-t border-green-deep03/[0.08] py-2 pl-3.5 pr-3 hover:bg-green-deep03/[0.03]'>
    {icon}
    <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
      <span className='truncate text-[14px] leading-[140%] text-text-primary'>{title}</span>
      <span className='text-[12px] leading-[140%] text-text-secondary'>{sub}</span>
    </span>
    {action}
  </div>
);

const tile = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg';

const useTeamFileCount = (live, slug) => {
  const [count, setCount] = useState(() =>
    live ? null : MOCK_RESOURCES.filter((f) => !f.pinned).length,
  );
  useEffect(() => {
    if (!live || !slug) return undefined;
    let alive = true;
    getBoardResources(slug)
      .then((list) => alive && setCount((list ?? []).filter((f) => !f.pinned).length))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [live, slug]);
  return count;
};

export const TaskInputs = ({ task, prevs, ctx, name, fmt, to }) => {
  const count = useTeamFileCount(ctx.live, ctx.slug);
  const leaderName = name([...ctx.memberById.values()].find((m) => m.role === 'leader')?.id);
  const files = task.files ?? [];
  return (
    <section aria-label='Inputs' className='flex flex-col gap-3'>
      <div className='flex flex-col gap-0.5'>
        <h2 className='text-[16px] font-medium leading-[150%] text-text-primary'>Inputs</h2>
        <span className='text-[13px] leading-[140%] text-text-secondary'>
          What this task starts from
        </span>
      </div>

      <InputCard
        head={<span className='text-[14px] font-medium leading-[140%] text-text-primary'>From PM</span>}
        meta={`${leaderName} · attached ${fmt(task.assignedAt)}`}
      >
        {files.length ? (
          files.map((f, i) => (
            <InputRow
              key={`${f.name}-${i}`}
              icon={
                <span
                  className={cn(
                    tile,
                    'bg-green-deep03/[0.06] text-[10px] font-medium uppercase tracking-[0.02em] text-green-deep04',
                  )}
                >
                  {extOf(f.name)}
                </span>
              }
              title={f.name}
              sub={`${f.kind} · ${typeof f.size === 'number' ? formatSize(f.size) : f.size}`}
              action={
                f.url ? (
                  <a href={f.url} download={f.name} target='_blank' rel='noreferrer' className={pill}>
                    <DownloadIcon className='h-4 w-4' /> Download
                  </a>
                ) : (
                  <span
                    title='Sample file — not downloadable in the demo'
                    className={cn(pill, 'cursor-not-allowed text-text-placeholder hover:bg-green-deep03/[0.08]')}
                  >
                    <DownloadIcon className='h-4 w-4' /> Download
                  </span>
                )
              }
            />
          ))
        ) : (
          <span className='px-4 pb-3.5 text-[14px] leading-[150%] text-text-tertiary'>
            No files attached
          </span>
        )}
      </InputCard>

      {prevs.length ? (
        prevs.map((p) => {
          const av = approvedVersionOf(p);
          return (
            <InputCard
              key={p.id}
              head={
                <span className='flex min-w-0 flex-col gap-0.5'>
                  <span className='text-[12px] leading-[140%] text-text-secondary'>
                    From earlier task
                  </span>
                  <Link
                    to={to(p.id)}
                    className='text-[14px] font-medium leading-[140%] text-text-primary underline decoration-grayscale-gray04 underline-offset-[3px] hover:text-green-deep03 hover:decoration-current'
                  >
                    {p.title}
                  </Link>
                </span>
              }
              meta={
                p.state === 'approved' ? (
                  <>
                    {name(av?.deliverable?.submittedBy ?? p.assigneeId)}
                    {av ? ` · v${av.v}` : ''} ·{' '}
                    <span className='text-functional-success04'>approved {fmt(approvedAt(p))}</span>
                  </>
                ) : (
                  `${stateLabelOf(p)} · not approved yet`
                )
              }
            >
              {av && (
                <div className='flex flex-col gap-2 border-t border-green-deep03/[0.08] px-4 pb-3.5 pt-2.5'>
                  <VersionBody deliverable={av.deliverable} rows={null} />
                </div>
              )}
            </InputCard>
          );
        })
      ) : (
        <div className='flex flex-col gap-0.5 rounded-xl border border-dashed border-green-deep03/[0.24] px-4 py-3'>
          <span className='text-[14px] font-medium leading-[140%] text-text-primary'>
            From earlier task
          </span>
          <span className='text-[13px] leading-[150%] text-text-secondary'>
            This task starts the relay. No earlier handoff.
          </span>
        </div>
      )}

      <InputCard
        head={<span className='text-[14px] font-medium leading-[140%] text-text-primary'>Resources</span>}
        meta='Team files'
      >
        <InputRow
          icon={
            <span className={cn(tile, 'bg-surface-green10')}>
              <PinIcon aria-hidden className='h-4 w-4 text-green-deep02' />
            </span>
          }
          title={ctx.data.project.briefTitle ?? 'Project brief'}
          sub='Pinned for every team'
          action={
            <button
              type='button'
              disabled={!ctx.scenarioRevealed}
              title={ctx.scenarioRevealed ? undefined : ctx.lockTip}
              onClick={ctx.openScenario}
              className={cn(pill, 'disabled:cursor-not-allowed disabled:text-text-placeholder')}
            >
              View
            </button>
          }
        />
        <InputRow
          icon={
            <span className={cn(tile, 'bg-green-deep03/[0.06]')}>
              <FolderIcon aria-hidden className='h-4 w-4 text-green-deep04' />
            </span>
          }
          title='Team files'
          sub={
            count == null
              ? 'Files and links for your team'
              : `${count} files and links for your team`
          }
          action={
            <Link to={`${ctx.base}/resources`} className={pill}>
              Open
            </Link>
          }
        />
      </InputCard>
    </section>
  );
};

export const MobileSummary = ({ rows }) => (
  <dl
    aria-label='Task summary'
    className='grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1.5 rounded-xl border border-green-deep03/[0.12] bg-grayscale-gray00 px-3.5 py-3 text-[13px] leading-[140%] tabletH:hidden'
  >
    {rows.map(([k, v]) => (
      <React.Fragment key={k}>
        <dt className='text-text-secondary'>{k}</dt>
        <dd className='m-0 min-w-0 text-text-primary [overflow-wrap:anywhere]'>{v}</dd>
      </React.Fragment>
    ))}
  </dl>
);

export const MobileActionBar = ({ title, primary, more, onMore, locked, lockTip }) =>
  primary ? (
    <div className='fixed inset-x-0 bottom-0 z-[800] flex items-center gap-2 border-t border-green-deep03/[0.12] bg-grayscale-gray00 px-4 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3 shadow-popup-01 tabletH:hidden'>
      <span className='line-clamp-2 min-w-0 flex-1 text-[13px] font-medium leading-[140%] text-text-primary'>
        {title}
      </span>
      {more && (
        <TdButton variant='ghost' onClick={onMore} className='h-11 shrink-0 px-3 font-normal'>
          More
        </TdButton>
      )}
      <TdButton
        variant='primary'
        onClick={primary.onClick}
        disabled={primary.disabled || locked}
        title={(locked ? lockTip : primary.tip) || undefined}
        className='h-11 shrink-0'
      >
        {primary.label}
      </TdButton>
    </div>
  ) : null;
